import * as React from 'react';
import { Download, ExternalLink, Eye, EyeOff, FilePlus2, FileText } from 'lucide-react';
import { DocLang } from '@clawhire/shared';
import { api } from '@/lib/api';
import { cn } from '@/lib/utils';
import { enumLabel, formatDate } from '@/lib/format';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { SkeletonLines } from '@/components/ui/skeleton';
import { toast } from '@/components/ui/toaster';
import type { FileRecord } from '@/components/data/FilesPanel';
import { ExternalLinkButton, LinkButton } from '@/pages/submissions/LinkButton';
import { summarizeCheckResults, versionLabel } from '@/pages/submissions/helpers';
import type { ProfileVersion, SubmissionDetail } from '@/pages/submissions/types';

export interface ProfilePanelProps {
  submission: SubmissionDetail;
  canEdit: boolean;
}

/**
 * The anonymised profile attached to this submission: current version (vNN, language, pages,
 * checks) with Preview / Download / Open in Drive, the version history, and a link to the
 * candidate page where new versions are generated.
 */
export function ProfilePanel({ submission: s, canEdit }: ProfilePanelProps) {
  const [previewId, setPreviewId] = React.useState<string | null>(null);
  const filesById = React.useMemo(() => new Map(s.profileFiles.map((f) => [f.id, f])), [s.profileFiles]);
  const current = s.currentProfileVersion;
  const others = s.profileVersions.filter((v) => v.id !== current?.id);

  const newProfile = canEdit ? (
    <LinkButton to={`/candidates/${s.candidate.id}`} variant="outline" size="sm">
      <FilePlus2 aria-hidden="true" /> New profile
    </LinkButton>
  ) : null;

  if (!current) {
    return (
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-muted-foreground">
          No profile version is linked to this submission yet. Generate one on the candidate page.
        </p>
        {newProfile}
      </div>
    );
  }

  const pdf = current.pdfFileId ? (filesById.get(current.pdfFileId) ?? null) : null;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <VersionSummary version={current} current />
        <div className="flex flex-wrap items-center gap-1">
          <VersionActions
            version={current}
            file={pdf}
            previewing={previewId === current.pdfFileId}
            onPreview={() => setPreviewId(previewId === current.pdfFileId ? null : current.pdfFileId)}
          />
          {newProfile}
        </div>
      </div>

      {previewId && (previewId === current.pdfFileId || others.some((v) => v.pdfFileId === previewId)) ? (
        <PdfFrame
          fileId={previewId}
          title={`Profile ${versionLabel(
            (previewId === current.pdfFileId ? current : others.find((v) => v.pdfFileId === previewId))
              ?.version ?? 0,
          )}`}
        />
      ) : null}

      {others.length > 0 ? (
        <div>
          <h4 className="mb-1.5 text-xs font-medium uppercase tracking-wide text-muted-foreground">
            Earlier versions
          </h4>
          <ul className="divide-y rounded-lg border">
            {others.map((v) => (
              <li key={v.id} className="flex flex-wrap items-center justify-between gap-2 px-3 py-2">
                <VersionSummary version={v} />
                <VersionActions
                  version={v}
                  file={v.pdfFileId ? (filesById.get(v.pdfFileId) ?? null) : null}
                  previewing={previewId === v.pdfFileId}
                  onPreview={() => setPreviewId(previewId === v.pdfFileId ? null : v.pdfFileId)}
                />
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </div>
  );
}

function VersionSummary({ version: v, current = false }: { version: ProfileVersion; current?: boolean }) {
  const checks = summarizeCheckResults(v.checkResults);
  return (
    <div className="min-w-0 text-sm">
      <div className="flex flex-wrap items-center gap-1.5">
        <FileText className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
        <span className="font-medium">{versionLabel(v.version)}</span>
        <span className="text-muted-foreground">{enumLabel(DocLang, v.lang)}</span>
        {v.pageCount !== null ? (
          <span className="text-muted-foreground">
            · {v.pageCount} {v.pageCount === 1 ? 'page' : 'pages'}
          </span>
        ) : null}
        {current ? <Badge variant="secondary">Current</Badge> : null}
        {v.forced ? (
          <Badge variant="outline" title={v.forceReason ?? undefined}>
            Forced
          </Badge>
        ) : null}
      </div>
      <div className="text-xs text-muted-foreground">
        {formatDate(v.createdAt)}
        {checks ? (
          <>
            {' · '}
            <span className={cn(checks.errors > 0 && 'text-destructive')}>
              {checks.errors === 0 && checks.warnings === 0
                ? 'checks passed'
                : `${checks.errors} error${checks.errors === 1 ? '' : 's'}, ${checks.warnings} warning${
                    checks.warnings === 1 ? '' : 's'
                  }`}
            </span>
          </>
        ) : null}
        {v.changeNote ? ` · ${v.changeNote}` : ''}
      </div>
    </div>
  );
}

function VersionActions({
  version: v,
  file,
  previewing,
  onPreview,
}: {
  version: ProfileVersion;
  file: FileRecord | null;
  previewing: boolean;
  onPreview: () => void;
}) {
  const label = versionLabel(v.version);
  if (!v.pdfFileId) return <span className="text-xs text-muted-foreground">No PDF</span>;
  const name = file?.name ?? `profile-${label}.pdf`;
  return (
    <div className="flex items-center gap-1">
      <Button
        variant="outline"
        size="sm"
        aria-pressed={previewing}
        aria-label={previewing ? `Hide preview of ${label}` : `Preview ${label}`}
        onClick={onPreview}
      >
        {previewing ? <EyeOff aria-hidden="true" /> : <Eye aria-hidden="true" />} Preview
      </Button>
      <Button
        variant="outline"
        size="sm"
        aria-label={`Download ${label}`}
        onClick={() => void downloadPdf(v.pdfFileId!, name)}
      >
        <Download aria-hidden="true" /> Download
      </Button>
      {file?.webViewLink ? (
        <ExternalLinkButton
          href={file.webViewLink}
          variant="outline"
          size="sm"
          aria-label={`Open ${label} in Drive`}
        >
          <ExternalLink aria-hidden="true" /> Open in Drive
        </ExternalLinkButton>
      ) : null}
    </div>
  );
}

async function downloadPdf(fileId: string, name: string): Promise<void> {
  try {
    const blob = await api.blob(`/files/${fileId}/content`, { query: { download: 1 } });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = name;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.setTimeout(() => URL.revokeObjectURL(url), 10_000);
  } catch (e) {
    toast.error(e instanceof Error ? e.message : 'Download failed');
  }
}

/** Fetches `/files/:id/content` with the bearer token and shows the PDF in an iframe. */
function PdfFrame({ fileId, title }: { fileId: string; title: string }) {
  const [url, setUrl] = React.useState<string | null>(null);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    let objectUrl: string | null = null;
    let cancelled = false;
    setUrl(null);
    setError(null);
    api
      .blob(`/files/${fileId}/content`)
      .then((blob) => {
        if (cancelled) return;
        objectUrl = URL.createObjectURL(blob.type ? blob : new Blob([blob], { type: 'application/pdf' }));
        setUrl(objectUrl);
      })
      .catch((e: Error) => {
        if (!cancelled) setError(e.message);
      });
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [fileId]);

  if (error) return <p className="text-sm text-destructive">Preview failed: {error}</p>;
  if (!url) return <SkeletonLines lines={3} />;
  return <iframe title={title} src={url} className="h-[70vh] w-full rounded-md border bg-muted" />;
}
